import React from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  UsersIcon,
  MapPinIcon,
  TrophyIcon,
  ClockIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';

interface Feature {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

interface FeaturesGridProps {
  features?: Feature[];
}

const DEFAULT_FEATURES: Feature[] = [
  {
    icon: ChartBarIcon,
    title: "Performance Analytics",
    description: "Track your weekly averages, identify trends, and see your improvement over time with interactive charts and detailed statistics."
  },
  {
    icon: UsersIcon,
    title: "League Comparisons",
    description: "Compare your performance against league averages and see how you rank among other players in your division."
  },
  {
    icon: MapPinIcon,
    title: "Lane Analysis",
    description: "Discover which lanes you perform best on and optimize your game strategy with detailed lane-by-lane breakdowns."
  },
  {
    icon: TrophyIcon,
    title: "Achievement Tracking",
    description: "Monitor your personal bests, track milestones, and celebrate your bowling achievements as you improve your game."
  },
  {
    icon: ClockIcon,
    title: "Historical Data",
    description: "Access comprehensive historical data to analyze long-term trends and understand your bowling journey over time."
  },
  {
    icon: DevicePhoneMobileIcon,
    title: "Mobile Optimized",
    description: "Access your stats anywhere with our fully responsive design that works perfectly on all your devices."
  }
];

export const FeaturesGrid: React.FC<FeaturesGridProps> = ({ 
  features = DEFAULT_FEATURES 
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut" as const
      }
    }
  };

  const iconVariants = {
    hover: {
      scale: 1.1,
      rotate: 5,
      transition: {
        duration: 0.3,
        ease: "easeOut" as const
      }
    }
  };

  const ctaVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.4
      }
    }
  };

  return (
    <section 
      className="py-16 sm:py-20 bg-white"
      aria-labelledby="features-heading"
      role="region"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2 
            id="features-heading"
            className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            variants={headerVariants}
          >
            Everything You Need to Improve Your Game
          </motion.h2>
          <motion.p 
            className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto"
            variants={headerVariants}
            aria-describedby="features-heading"
          >
            Get comprehensive insights into your bowling performance with our 
            professional-grade analytics and tracking tools.
          </motion.p>
        </motion.div>

        {/* Features Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 auto-rows-fr"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          role="list"
          aria-label="Platform features"
        >
          {features.map((feature, index) => (
            <motion.article
              key={index}
              variants={cardVariants}
              whileHover="hover"
              className="group relative bg-white p-6 sm:p-8 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 ease-in-out focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 h-full flex flex-col"
              role="listitem"
              tabIndex={0}
              aria-labelledby={`feature-title-${index}`}
              aria-describedby={`feature-desc-${index}`}
            >
              {/* Icon */}
              <motion.div 
                className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-blue-100 rounded-lg mb-4 sm:mb-6 group-hover:bg-blue-200 transition-colors duration-300"
                variants={iconVariants}
                aria-hidden="true"
              >
                <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
              </motion.div>

              {/* Content */}
              <div className="flex-1 flex flex-col">
                <h3 
                  id={`feature-title-${index}`}
                  className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 group-hover:text-blue-900 transition-colors duration-300"
                >
                  {feature.title}
                </h3>
                <p 
                  id={`feature-desc-${index}`}
                  className="text-gray-600 text-sm sm:text-base leading-relaxed group-hover:text-gray-700 transition-colors duration-300 flex-1"
                >
                  {feature.description}
                </p>
              </div>

              {/* Hover Effect Overlay */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-50/0 to-blue-100/0 group-hover:from-blue-50/20 group-hover:to-blue-100/10 transition-all duration-300 pointer-events-none" aria-hidden="true" />
            </motion.article>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-12 sm:mt-16"
          variants={ctaVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <p className="text-gray-600 mb-6">
            Ready to take your bowling to the next level?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto sm:max-w-none">
            <motion.button 
              className="w-full sm:w-auto min-w-[180px] min-h-[48px] bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation flex items-center justify-center"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
              aria-label="Start tracking your bowling performance now"
            >
              Start Tracking Now
            </motion.button>
            <motion.button 
              className="w-full sm:w-auto min-w-[180px] min-h-[48px] text-blue-600 hover:text-blue-700 px-8 py-3 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation flex items-center justify-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              aria-label="View a demonstration of the platform features"
            >
              View Demo
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesGrid;